import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import SearchRequests from "../classes/SearchRequests";
import ListWrapper from "../utils/ListWrapper";
import SearchRequestsRequest from "../models/SearchRequestsRequest";
import Administrator from "../models/Administrator";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import SearchRequest from "../models/SearchRequest";
import RequestStatus from "../classes/RequestStatus";
import DateTimeFormatView from "./DateTimeFormatView";
import CollectionUtils from "../utils/CollectionUtils";
import NOKUser from "../models/NOKUser";
import Query from "../classes/Query";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface PendingRequestsViewProps extends BaseUIProps {
  key?: string;
  user: NOKUser;
  admin: Administrator;
}

class _PendingRequestsViewState extends ObservableComponent<PendingRequestsViewProps> {
  static defaultProps = { user: null, admin: null };
  query: SearchRequests = null;
  requests: Array<SearchRequest> = ListWrapper.widget(this, "requests");
  request: SearchRequestsRequest = null;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: PendingRequestsViewProps) {
    super(props);

    this.initState();
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      [
        "request",
        "request.applyStatus",
        "request.ascending",
        "request.createdBy",
        "request.fromDate",
        "request.hospital",
        "request.name",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
        "request.request",
        "request.status",
        "request.toDate",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.updateSyncProperty("user", this.props.user);

    this.updateSyncProperty("admin", this.props.admin);

    this.on(["query", "query.items"], this.computeRequests);

    this.computeRequests();

    this.on(
      [
        "requests",
        "requests.hospital",
        "requests.hospital.name",
        "requests.requestDate",
        "requests.searchId",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: PendingRequestsViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public setQuery(val: SearchRequests): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getSearchRequests(
          UsageConstants.QUERY_GETSEARCHREQUESTS_PENDINGREQUESTSVIEW_PROPERTIES_QUERY_COMPUTATION,
          this.request
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setRequests(val: Array<SearchRequest>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.requests, val);

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("requests", this.requests, val);

    this.requests.clear();

    this.requests.addAll(val);

    this.fire("requests", this);
  }
  public addToRequests(val: SearchRequest, index: number = -1): void {
    if (index === -1) {
      if (!this.requests.contains(val)) this.requests.add(val);
    } else {
      this.requests.remove(this.requests.elementAt(index));

      this.requests.add(val);
    }

    this.fire("requests", this, val, true);

    this.updateObservable("requests", null, val);
  }
  public removeFromRequests(val: SearchRequest): void {
    this.requests.remove(val);

    this.fire("requests", this, val, false);

    this.removeObservable("requests", val);
  }
  public computeRequests = (): void => {
    try {
      this.setRequests(Array.from(this.query?.items ?? []));
    } catch (exception) {
      console.log(" exception in computeRequests : " + exception.toString());

      this.setRequests([]);
    }
  };
  public setRequest(val: SearchRequestsRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            TextView({
              data: "Pending Requests",
              style: new ui.TextStyle({
                fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
              }),
              key: "0",
            }),
            this.requests.length !== 0
              ? TextView({
                  data: this.requests.length.toString(),
                  style: new ui.TextStyle({
                    fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                    fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                  }),
                })
              : [],
          ],
          className: "x928 hc h",
          key: "0",
        }),
        this.requests.length > 0
          ? ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                ui.Table({
                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                  columnWidths: {
                    0: new ui.FlexColumnWidth(1),
                    1: new ui.FlexColumnWidth(1),
                    2: new ui.FlexColumnWidth(1),
                  },
                  children: [
                    ui.TableRow({
                      children: [
                        ui.TableCell({
                          child: TextView({
                            data: "Request ID",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "0",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Hospital",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "1",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Requested Date",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "2",
                        }),
                      ],
                      className: "x412 hc",
                      key: "0",
                    }),
                    this.requests.expand((item) => [
                      ui.TableRow({
                        children: [
                          ui.TableCell({
                            child: TextView({
                              data: item?.searchId ?? "",
                              softWrap: true,
                              style: new ui.TextStyle({
                                fontFamily:
                                  cStyle.tTextViewTableCellTextFontFamilyOn,
                                color: cStyle.tTextViewTableCellTextColorOn,
                                fontSize:
                                  cStyle.tTextViewTableCellTextFontSizeOn,
                              }),
                              className: "hc",
                            }),
                            className: "hc",
                            key: "0",
                          }),
                          ui.TableCell({
                            child: TextView({
                              data: item?.hospital?.name ?? "",
                              softWrap: true,
                              style: new ui.TextStyle({
                                fontFamily:
                                  cStyle.tTextViewTableCellTextFontFamilyOn,
                                color: cStyle.tTextViewTableCellTextColorOn,
                                fontSize:
                                  cStyle.tTextViewTableCellTextFontSizeOn,
                              }),
                              className: "hc",
                            }),
                            className: "hc",
                            key: "1",
                          }),
                          ui.TableCell({
                            child: DateTimeFormatView({
                              value: item.requestDate,
                              timeFormat: "hh:mm a EEE, dd MMM yyyy",
                              dateFormat: "dd/MM/yyyy",
                              icon: MaterialIcons.access_time,
                              className: "xc64 hc h",
                            }),
                            className: "x0d5 hc h",
                            key: "2",
                          }),
                        ],
                        className: "x50a hc h",
                        key: item?.ident,
                      }),
                    ]),
                  ],
                  className: "x00b hc h",
                  key: "0",
                }),
              ],
              className: "x2da hc h",
            })
          : ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.center,
              mainAxisAlignment: ui.MainAxisAlignment.center,
              children: [
                ui.AssetImage({
                  path: "images/emptyList.png",
                  width: 300,
                  height: 300,
                  className: "x2b1 hc vc",
                  key: "0",
                }),
                TextView({
                  data: "No Pending Requests",
                  style: new ui.TextStyle({
                    fontWeight: ui.FontWeight.bold,
                    fontSize: 16,
                  }),
                  className: "x261 hc",
                  key: "1",
                }),
              ],
              className: "x52f hc",
            }),
      ],
      className: ui.join(
        this.props.className,
        "PendingRequestsView x0e7 hc vc h"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    if (this.user !== null) {
      this.setRequest(
        new SearchRequestsRequest({
          applyStatus: false,
          status: RequestStatus.Processing,
          pageSize: 100,
          orderBy: "",
          ascending: true,
          offset: 0,
          hospital: this.user.hospital,
        })
      );
    } else if (this.admin !== null) {
      this.setRequest(
        new SearchRequestsRequest({
          applyStatus: false,
          status: RequestStatus.Processing,
          pageSize: 100,
          orderBy: "",
          ascending: true,
          offset: 0,
        })
      );
    }
  };
}
export default function PendingRequestsView(props: PendingRequestsViewProps) {
  return React.createElement(_PendingRequestsViewState, {
    ..._PendingRequestsViewState.defaultProps,
    ...props,
  });
}
